import { AMOwnershipParcelComplete } from 'src/common-services'
import {
  MAX_CONTENT_WIDTH,
  SidebarResultOverlay,
  SidebarResultOverlayProps,
} from 'src/v2/components/sidebar-result-overlay'
import { SidebarResultOverlayHeader } from 'src/v2/components/sidebar-result-overlay-header'
import { MapSquareIcon } from 'src/components/icons/map-square'
import { Colors, Shapes } from 'src/components/theme'
import styled from '@emotion/styled'
import { formatString } from 'src/utils/format-string'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { useAtomValue } from 'jotai'
import * as coreAtoms from 'src/v2/state/core'
import { isFeatureEnabled } from 'src/utils/feature-flag'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { DedupeResults } from 'src/views/map-explorer/sidebar/related-results/types'
import { PlacesResultsList } from 'src/views/map-explorer/sidebar/related-results/places-result-list'
import { parcelFullAddress } from 'src/views/map-explorer/sidebar/panels/selected-parcel'
import { PARCEL_TYPES_BY_MCODE_MAP, PARCEL_TYPES_MAP } from 'src/constants/cherre-data-dictionary/zoning-colors'
import dynamic from 'next/dynamic'

// As the map is being rendered in a Popper it errors out with next js server side rendering.
// So we need to explicitly render the map on the client.
const ParcelPlacesMap = dynamic(
  () => import('src/views/map-explorer/sidebar/related-results/parcel-places-map').then((mod) => mod.ParcelPlacesMap),
  {
    ssr: false,
  }
)

const ExtraContentWrapper = styled.div`
  padding-top: 8px;
`

const ParcelResultOverlayContent = styled.div`
  width: ${MAX_CONTENT_WIDTH}px;
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[2]}px;
`

type ParcelOverlayProps = {
  parcel?: AMOwnershipParcelComplete
  anchorEl: SidebarResultOverlayProps['anchorEl']
  onClose: SidebarResultOverlayProps['onClose']
  open: SidebarResultOverlayProps['open']
  placesWithinParcelResults?: DedupeResults
}

export const ParcelOverlay: React.FC<ParcelOverlayProps> = ({
  anchorEl,
  open,
  onClose,
  parcel,
  placesWithinParcelResults,
}) => {
  const selectedEntity = useAtomValue(coreAtoms.selectedEntity)
  const selectedEntityId = useAtomValue(coreAtoms.selectedEntityId)
  const ownerRevealEnabled = isFeatureEnabled('owner-reveal')
  const { selectParcel } = useMapExplorerNavigation()

  const onOpenParcel = async () => {
    if (parcel?.id && selectedEntity?.type !== 'SELECTED_PARCEL') {
      await selectParcel(parcel.id)
    }
  }

  if (!parcel) {
    return null
  }

  return (
    <SidebarResultOverlay
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      header={
        <SidebarResultOverlayHeader
          title={formatString(
            parcelFullAddress(parcel.address, parcel.city, parcel.state || '', parcel.zip),
            'address'
          )}
          subtitle={`${PARCEL_TYPES_MAP.get(PARCEL_TYPES_BY_MCODE_MAP[parcel.mCode || ''])?.label ?? ''}`}
          icon={<MapSquareIcon size={14} />}
          iconShape="square"
          iconBgColor={Colors.primaryGreen700}
          iconBorderColor={Colors.primaryGreen900}
          iconSize={40}
          extraContent={
            selectedEntity?.type !== 'SELECTED_PARCEL' && ownerRevealEnabled ? (
              <ExtraContentWrapper>
                <ButtonWithIcon inverted onClick={onOpenParcel}>
                  Open Parcel
                </ButtonWithIcon>
              </ExtraContentWrapper>
            ) : undefined
          }
        />
      }>
      <ParcelResultOverlayContent>
        <ParcelPlacesMap parcel={parcel} results={placesWithinParcelResults} />
        <PlacesResultsList results={placesWithinParcelResults} selectedEntityId={selectedEntityId} />
      </ParcelResultOverlayContent>
    </SidebarResultOverlay>
  )
}
